'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import React from 'react';
import { Button, Panel, Toggle } from 'ui';
import { forceEbaySync, postSyncStatus } from '~/api/ebay';
import NewTabLink from '~/components/Links/NewTabLink';
import { RailsItem } from '~/typings/services/rails/item';

interface Props {
  item: RailsItem;
}

const EBayWidget: React.FC<Props> = ({ item }) => {
  /*
  const { user } = useSession();
  const { data: session } = useV1Session();
  const hasFeature = !!session?.user?.features?.find(
    f => f.slug === 'ebay_integration',
  );

  const { data: integrationState } = useQuery(
    user && hasFeature && 'ebay_integration',
    () => api.ebay.integrationState(user!.username),
    {
      retry: false,
    },
  );*/
  const queryClient = useQueryClient();

  const mutationOptions = {
    onSuccess: async () => {
      await queryClient.invalidateQueries(['items', item.id]);
    },
  };

  const { status: forceSyncStatus, mutate: forceSync } = useMutation(
    () => forceEbaySync(item.id),
    mutationOptions,
  );

  const { status: syncStatus, mutate: setSyncStatus } = useMutation(
    (sync: boolean) => postSyncStatus(item.id, sync),
    mutationOptions,
  );

  const loading = forceSyncStatus === 'loading' || syncStatus === 'loading';

  if (!item.ebay) {
    return null;
  }

  return (
    <Panel className="mb-4">
      <div className="mb-2 text-base font-semibold">eBay Info</div>
      <div className="flex items-center space-x-2">
        <b>eBay Item ID</b>
        <div>
          <NewTabLink className="hover:underline" href={item.ebay?.url}>
            {item.ebay?.id}
          </NewTabLink>
        </div>
      </div>

      <div className="my-2 flex items-center space-x-2">
        <b>Sync:</b>
        <div>
          <Toggle
            disabled={loading}
            onChange={enabled => setSyncStatus(enabled)}
            value={item.ebay?.sync}
          />
        </div>
      </div>

      <div className="mb-2">
        Last Synced:{' '}
        {item.ebay.last_synced_at
          ? formatDistanceToNow(new Date(item.ebay.last_synced_at), {
              addSuffix: true,
            })
          : 'never'}
      </div>

      <Button
        disabled={!item.actions.includes('sync_ebay') || loading}
        size="small"
        className="w-full"
        color="black"
        onClick={() => forceSync()}
      >
        Sync Now
      </Button>
    </Panel>
  );
};

export default EBayWidget;
